import { type CSSProperties } from 'typestyle/lib/types';
import { cssRaw, style } from 'typestyle';
import DashboardTheme from '../dashboard-theme';

export const headerRow = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: 'space-between',
  alignItems: isMobile ? 'stretch' : 'center',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 20,
});

export const selectorRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

export const selectorLabel: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  color: DashboardTheme.navy800,
};

export const officeSelect: CSSProperties = {
  padding: 8,
  paddingLeft: 16,
  paddingRight: 16,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.navy800,
  height: 36,
  minWidth: 220,
  backgroundColor: DashboardTheme.white,
};

export const newButtonWrap: CSSProperties = {
  width: 180,
};

export const sectionCard: CSSProperties = {
  padding: '18px 20px',
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: DashboardTheme.radius,
  boxShadow: DashboardTheme.shadow,
  marginBottom: 20,
};

export const sectionTitle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  fontFamily: DashboardTheme.bodyFontFamily,
  color: DashboardTheme.navy800,
  marginBottom: 12,
};

export const emptyStateWrap: CSSProperties = {
  padding: '48px 16px',
  textAlign: 'center',
};

export const emptyStateTitle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  fontFamily: DashboardTheme.headerFontFamily,
  color: DashboardTheme.navy900,
  marginBottom: 8,
};

export const emptyStateDescription: CSSProperties = {
  fontSize: 13,
  color: DashboardTheme.gray700,
  marginBottom: 20,
  maxWidth: 420,
  marginLeft: 'auto',
  marginRight: 'auto',
};

export const emptyStateButtonWrap: CSSProperties = {
  width: 200,
  marginLeft: 'auto',
  marginRight: 'auto',
};

// A stable class (not just a style object) so the row-hover rule below can be scoped to it.
export const tableWrap = style({
  overflowX: 'auto',
  minWidth: 0,
  borderRadius: 12,
  border: `1px solid ${DashboardTheme.gray200}`,
});

cssRaw(
  `
  .${tableWrap} tbody tr {
    transition: background-color .12s ease;
  }
  .${tableWrap} tbody tr:hover {
    background-color: ${DashboardTheme.gray50};
  }
  `,
);

export const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

export const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: DashboardTheme.gray700,
  background: DashboardTheme.gray50,
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  padding: '10px 12px',
};

export const td: CSSProperties = {
  fontSize: 13,
  color: DashboardTheme.navy800,
  borderBottom: `1px solid ${DashboardTheme.gray100}`,
  padding: '10px 12px',
  verticalAlign: 'middle',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const statusBadge = (status: 'pending' | 'live' | 'paused' | 'revoked'): CSSProperties => {
  const palette: Record<typeof status, { bg: string; color: string }> = {
    pending: { bg: DashboardTheme.amber100, color: DashboardTheme.amber600 },
    live: { bg: DashboardTheme.green100, color: DashboardTheme.green600 },
    paused: { bg: DashboardTheme.gray100, color: DashboardTheme.gray700 },
    revoked: { bg: 'rgba(221, 75, 57, 0.12)', color: DashboardTheme.red500 },
  };

  return {
    fontSize: 10.5,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 999,
    background: palette[status].bg,
    color: palette[status].color,
    whiteSpace: 'nowrap',
  };
};

export const actionsCell: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

export const smallButton: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: '5px 10px',
  borderRadius: 6,
  border: `1px solid ${DashboardTheme.gray200}`,
  background: DashboardTheme.white,
  color: DashboardTheme.navy700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const smallButtonDanger: CSSProperties = {
  ...smallButton,
  color: DashboardTheme.red500,
  borderColor: 'rgba(221, 75, 57, 0.35)',
};

export const pairingInput: CSSProperties = {
  padding: '5px 8px',
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 6,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12,
  color: DashboardTheme.navy800,
  width: 120,
  textTransform: 'uppercase',
};

export const errorText: CSSProperties = {
  fontSize: 11,
  color: DashboardTheme.red500,
  marginTop: 4,
};

export const savedText: CSSProperties = {
  fontSize: 11,
  color: DashboardTheme.green600,
  marginTop: 4,
};

export const behaviorPanel: CSSProperties = {
  marginTop: 8,
  padding: 12,
  background: DashboardTheme.gray50,
  borderRadius: 8,
  border: `1px solid ${DashboardTheme.gray200}`,
};

export const formRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  marginBottom: 12,
};

export const formField: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minWidth: 200,
};

export const fieldLabel: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: DashboardTheme.navy800,
};

export const textInput: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 6,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13,
  color: DashboardTheme.navy800,
  height: 36,
};

export const numberInput: CSSProperties = {
  ...textInput,
  width: 100,
};

export const selectInput: CSSProperties = {
  ...textInput,
  backgroundColor: DashboardTheme.white,
};

export const checkboxGroup: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

export const checkboxRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: DashboardTheme.navy800,
};

// A view this office cannot fill with data: greyed out and not clickable, with the reason alongside.
export const checkboxRowDisabled: CSSProperties = {
  color: DashboardTheme.gray500,
  cursor: 'not-allowed',
};

// Why a view can't be picked for this office (no parkings, catering off), next to its checkbox.
export const checkboxHint: CSSProperties = {
  fontSize: 12,
  fontStyle: 'italic',
  color: DashboardTheme.gray500,
};

export const segmentedControl: CSSProperties = {
  display: 'flex',
  gap: 4,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 8,
  padding: 3,
  width: 'fit-content',
};

export const segmentedOption = (active: boolean): CSSProperties => ({
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: DashboardTheme.bodyFontFamily,
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  background: active ? DashboardTheme.navy800 : 'transparent',
  color: active ? DashboardTheme.white : DashboardTheme.navy700,
  whiteSpace: 'nowrap',
});

export const buttonRow: CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  flexWrap: 'wrap',
};

export const buttonWrap: CSSProperties = {
  width: 160,
};

export const emptyMessage: CSSProperties = {
  color: DashboardTheme.gray500,
  fontSize: 13,
};

export const linkCopyGroup: CSSProperties = {
  display: 'flex',
  gap: 4,
};

export const linkCopyButton: CSSProperties = {
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 11,
  fontWeight: 700,
  color: DashboardTheme.navy800,
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 6,
  padding: '5px 10px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const publicToggleCell: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
};

// Hand-rolled switch rather than a checkbox: "public access" is a state the display is in, not a
// deletion-style action, and the track/knob reads as on-or-off at a glance across a list of rows.
// Built on a real <button role="switch"> so keyboard and screen-reader behaviour come for free.
export const publicToggleTrack = (isPublic: boolean, disabled: boolean): CSSProperties => ({
  position: 'relative',
  flexShrink: 0,
  width: 34,
  height: 18,
  padding: 0,
  border: 'none',
  borderRadius: 9,
  background: isPublic ? DashboardTheme.green600 : DashboardTheme.gray200,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  transition: 'background 150ms ease',
});

export const publicToggleKnob = (isPublic: boolean): CSSProperties => ({
  position: 'absolute',
  top: 2,
  left: isPublic ? 18 : 2,
  width: 14,
  height: 14,
  borderRadius: '50%',
  background: DashboardTheme.white,
  transition: 'left 150ms ease',
});

// A shared link means anyone holding the URL can read this office's occupancy without logging in,
// so the "on" state is deliberately loud rather than quiet default-styled label text.
export const publicToggleLabel = (isPublic: boolean): CSSProperties => ({
  fontSize: 11,
  fontWeight: isPublic ? 700 : 400,
  color: isPublic ? DashboardTheme.navy800 : DashboardTheme.gray500,
});
